import type { ReactNode } from "react";
import Link from "next/link";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ProductCardDisplay } from "./product-card-display";
import {
  CardArt,
  CardBody,
  CardFacts,
  CardPrice,
  CardSpec,
} from "./product-card-parts";

const CARD_CLASS =
  "group group/card relative h-full min-w-0 overflow-hidden rounded-lg shadow-card transition-shadow duration-150 hover:shadow-pop max-md:rounded-none max-md:bg-transparent max-md:shadow-none";

const PHONE_ROW_CARD_CLASS =
  "max-md:grid max-md:grid-cols-[6rem_minmax(0,1fr)_auto] max-md:items-center max-md:gap-x-3.5 max-md:px-4 max-md:py-2";

function CardAction({
  display,
  quoteHref,
}: {
  display: ProductCardDisplay;
  quoteHref?: string;
}) {
  const toQuote = !display.inStock && !display.isFreePickup && quoteHref;
  return (
    <Button
      asChild
      variant="primary"
      className="relative z-20 mt-auto w-full max-md:hidden"
    >
      <Link
        prefetch={false}
        href={toQuote ? quoteHref : display.href}
        aria-label={`${toQuote ? CHECK_AVAILABILITY_LABEL : "View"} — ${display.name || "this listing"}`}
      >
        {toQuote ? CHECK_AVAILABILITY_LABEL : "View"}
      </Link>
    </Button>
  );
}

function CardMedia({
  display,
  priority,
  sizes,
  saveSlot,
}: {
  display: ProductCardDisplay;
  priority: boolean;
  sizes?: string;
  saveSlot?: ReactNode;
}) {
  return (
    <div className="relative group-data-[phone-row]/card:max-md:contents">
      <CardArt
        imageUrl={display.imageUrl}
        title={display.name}
        grade={display.grade}
        productTypeSlug={display.productTypeSlug}
        priority={priority}
        sizes={sizes}
      />
      {saveSlot ? (
        <div className="absolute right-2.5 top-2.5 z-20 max-md:right-2 max-md:top-2 group-data-[phone-row]/card:max-md:static group-data-[phone-row]/card:max-md:col-start-3 group-data-[phone-row]/card:max-md:row-start-1">
          {saveSlot}
        </div>
      ) : null}
    </div>
  );
}

export function ProductCardView({
  display,
  priority = false,
  sizes,
  saveSlot,
  quoteHref,
  phoneRow = false,
}: {
  display: ProductCardDisplay;
  priority?: boolean;
  sizes?: string;
  saveSlot?: ReactNode;
  quoteHref?: string;
  phoneRow?: boolean;
}) {
  return (
    <Card
      variant="plain"
      padding="none"
      className={cn(CARD_CLASS, phoneRow && PHONE_ROW_CARD_CLASS)}
      data-phone-row={phoneRow ? "" : undefined}
    >
      <CardMedia display={display} priority={priority} sizes={sizes} saveSlot={saveSlot} />
      <CardBody>
        <CardSpec productName={display.name} dimensions={display.dimensions} href={display.href} />
        <CardPrice unitPrice={display.unitPrice} freeLabel={display.freeLabel} />
        <CardFacts
          location={display.location}
          quantityAvailable={display.quantityAvailable}
          inStock={display.inStock}
          pickupOnly={display.pickupOnly}
        />
        <CardAction display={display} quoteHref={quoteHref} />
      </CardBody>
    </Card>
  );
}
